export interface TourStep {
  /** CSS selektor celu kroku (np. `[data-tour="bookkeeping-table"]`). Brak = krok wyśrodkowany. */
  selector?: string;
  titleKey: string;
  bodyKey: string;
  /** Czy ten krok symuluje zapis (pokaż „to symulacja, nic nie zapisano"). */
  simulatesSave?: boolean;
}

export interface TourDefinition {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: string;
  /** Trasa, na której działa samouczek (ścieżka bez query). */
  route: string;
  /** Klucz grupy modułu na stronie Samouczek. */
  group: string;
  steps: TourStep[];
  /** Czy oferowany jest wariant „symulacja bez zapisu". */
  sandbox?: boolean;
}

/**
 * Rejestr samouczków panelu Księgowość. Selektory kroków celują w atrybuty
 * `data-tour="..."` obecne na docelowych ekranach. Treść kroków tłumaczona transloco.
 */
export const TOUR_REGISTRY: TourDefinition[] = [
  {
    id: 'dashboard',
    titleKey: 'tour.dashboard.title',
    descriptionKey: 'tour.dashboard.description',
    icon: 'pi pi-gauge',
    route: '/',
    group: 'overview',
    steps: [
      { titleKey: 'tour.dashboard.steps.intro.title', bodyKey: 'tour.dashboard.steps.intro.body' },
      { selector: '[data-tour="dashboard-primary"]', titleKey: 'tour.dashboard.steps.primary.title', bodyKey: 'tour.dashboard.steps.primary.body' },
      { selector: '[data-tour="dashboard-main"]', titleKey: 'tour.dashboard.steps.main.title', bodyKey: 'tour.dashboard.steps.main.body' },
    ],
  },
  {
    id: 'bookkeeping',
    titleKey: 'tour.bookkeeping.title',
    descriptionKey: 'tour.bookkeeping.description',
    icon: 'pi pi-book',
    route: '/bookkeeping',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.bookkeeping.steps.intro.title', bodyKey: 'tour.bookkeeping.steps.intro.body' },
      { selector: '[data-tour="bookkeeping-table"]', titleKey: 'tour.bookkeeping.steps.table.title', bodyKey: 'tour.bookkeeping.steps.table.body' },
    ],
  },
  {
    id: 'chartOfAccounts',
    titleKey: 'tour.chartOfAccounts.title',
    descriptionKey: 'tour.chartOfAccounts.description',
    icon: 'pi pi-sitemap',
    route: '/ledger',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.chartOfAccounts.steps.intro.title', bodyKey: 'tour.chartOfAccounts.steps.intro.body' },
      { selector: '[data-tour="chart-mode"]', titleKey: 'tour.chartOfAccounts.steps.mode.title', bodyKey: 'tour.chartOfAccounts.steps.mode.body' },
      { selector: '[data-tour="chart-add"]', titleKey: 'tour.chartOfAccounts.steps.add.title', bodyKey: 'tour.chartOfAccounts.steps.add.body' },
      { selector: '[data-tour="chart-table"]', titleKey: 'tour.chartOfAccounts.steps.table.title', bodyKey: 'tour.chartOfAccounts.steps.table.body' },
    ],
  },
  {
    id: 'journal',
    titleKey: 'tour.journal.title',
    descriptionKey: 'tour.journal.description',
    icon: 'pi pi-book',
    route: '/ledger/dziennik',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.journal.steps.intro.title', bodyKey: 'tour.journal.steps.intro.body' },
      { selector: '[data-tour="journal-period"]', titleKey: 'tour.journal.steps.period.title', bodyKey: 'tour.journal.steps.period.body' },
      { selector: '[data-tour="journal-add"]', titleKey: 'tour.journal.steps.add.title', bodyKey: 'tour.journal.steps.add.body' },
      { selector: '[data-tour="journal-table"]', titleKey: 'tour.journal.steps.table.title', bodyKey: 'tour.journal.steps.table.body' },
    ],
  },
  {
    id: 'generalLedger',
    titleKey: 'tour.generalLedger.title',
    descriptionKey: 'tour.generalLedger.description',
    icon: 'pi pi-calculator',
    route: '/ledger/obroty-salda',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.generalLedger.steps.intro.title', bodyKey: 'tour.generalLedger.steps.intro.body' },
      { selector: '[data-tour="general-ledger-period"]', titleKey: 'tour.generalLedger.steps.period.title', bodyKey: 'tour.generalLedger.steps.period.body' },
      { selector: '[data-tour="general-ledger-balance"]', titleKey: 'tour.generalLedger.steps.balance.title', bodyKey: 'tour.generalLedger.steps.balance.body' },
      { selector: '[data-tour="general-ledger-table"]', titleKey: 'tour.generalLedger.steps.table.title', bodyKey: 'tour.generalLedger.steps.table.body' },
    ],
  },
  {
    id: 'kpir',
    titleKey: 'tour.kpir.title',
    descriptionKey: 'tour.kpir.description',
    icon: 'pi pi-table',
    route: '/ledger/kpir',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.kpir.steps.intro.title', bodyKey: 'tour.kpir.steps.intro.body' },
      { selector: '[data-tour="kpir-scope"]', titleKey: 'tour.kpir.steps.scope.title', bodyKey: 'tour.kpir.steps.scope.body' },
      { selector: '[data-tour="kpir-period"]', titleKey: 'tour.kpir.steps.period.title', bodyKey: 'tour.kpir.steps.period.body' },
      { selector: '[data-tour="kpir-table"]', titleKey: 'tour.kpir.steps.table.title', bodyKey: 'tour.kpir.steps.table.body' },
      { selector: '[data-tour="kpir-totals"]', titleKey: 'tour.kpir.steps.totals.title', bodyKey: 'tour.kpir.steps.totals.body' },
      { selector: '[data-tour="kpir-export"]', titleKey: 'tour.kpir.steps.export.title', bodyKey: 'tour.kpir.steps.export.body' },
    ],
  },
  {
    id: 'rzis',
    titleKey: 'tour.rzis.title',
    descriptionKey: 'tour.rzis.description',
    icon: 'pi pi-chart-line',
    route: '/ledger/rzis',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.rzis.steps.intro.title', bodyKey: 'tour.rzis.steps.intro.body' },
      { selector: '[data-tour="rzis-period"]', titleKey: 'tour.rzis.steps.period.title', bodyKey: 'tour.rzis.steps.period.body' },
      { selector: '[data-tour="rzis-table"]', titleKey: 'tour.rzis.steps.table.title', bodyKey: 'tour.rzis.steps.table.body' },
      { selector: '[data-tour="rzis-result"]', titleKey: 'tour.rzis.steps.result.title', bodyKey: 'tour.rzis.steps.result.body' },
      { selector: '[data-tour="rzis-export"]', titleKey: 'tour.rzis.steps.export.title', bodyKey: 'tour.rzis.steps.export.body' },
    ],
  },
  {
    id: 'bilans',
    titleKey: 'tour.bilans.title',
    descriptionKey: 'tour.bilans.description',
    icon: 'pi pi-images',
    route: '/ledger/bilans',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.bilans.steps.intro.title', bodyKey: 'tour.bilans.steps.intro.body' },
      { selector: '[data-tour="bilans-asof"]', titleKey: 'tour.bilans.steps.asof.title', bodyKey: 'tour.bilans.steps.asof.body' },
      { selector: '[data-tour="bilans-balance"]', titleKey: 'tour.bilans.steps.balance.title', bodyKey: 'tour.bilans.steps.balance.body' },
      { selector: '[data-tour="bilans-tables"]', titleKey: 'tour.bilans.steps.tables.title', bodyKey: 'tour.bilans.steps.tables.body' },
      { selector: '[data-tour="bilans-export"]', titleKey: 'tour.bilans.steps.export.title', bodyKey: 'tour.bilans.steps.export.body' },
    ],
  },
  {
    id: 'openingBalances',
    titleKey: 'tour.openingBalances.title',
    descriptionKey: 'tour.openingBalances.description',
    icon: 'pi pi-flag-fill',
    route: '/ledger/bilans-otwarcia',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.openingBalances.steps.intro.title', bodyKey: 'tour.openingBalances.steps.intro.body' },
      { selector: '[data-tour="opening-balances-year"]', titleKey: 'tour.openingBalances.steps.year.title', bodyKey: 'tour.openingBalances.steps.year.body' },
      { selector: '[data-tour="opening-balances-table"]', titleKey: 'tour.openingBalances.steps.table.title', bodyKey: 'tour.openingBalances.steps.table.body' },
      { selector: '[data-tour="opening-balances-balance"]', titleKey: 'tour.openingBalances.steps.balance.title', bodyKey: 'tour.openingBalances.steps.balance.body' },
      { selector: '[data-tour="opening-balances-save"]', titleKey: 'tour.openingBalances.steps.save.title', bodyKey: 'tour.openingBalances.steps.save.body', simulatesSave: true },
    ],
  },
  {
    id: 'periods',
    titleKey: 'tour.periods.title',
    descriptionKey: 'tour.periods.description',
    icon: 'pi pi-calendar-clock',
    route: '/ledger/okresy',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.periods.steps.intro.title', bodyKey: 'tour.periods.steps.intro.body' },
      { selector: '[data-tour="periods-year"]', titleKey: 'tour.periods.steps.year.title', bodyKey: 'tour.periods.steps.year.body' },
      { selector: '[data-tour="periods-table"]', titleKey: 'tour.periods.steps.table.title', bodyKey: 'tour.periods.steps.table.body' },
      { selector: '[data-tour="periods-table"]', titleKey: 'tour.periods.steps.action.title', bodyKey: 'tour.periods.steps.action.body' },
    ],
  },
  {
    id: 'autoPosting',
    titleKey: 'tour.autoPosting.title',
    descriptionKey: 'tour.autoPosting.description',
    icon: 'pi pi-bolt',
    route: '/ledger/auto-ksiegowania',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.autoPosting.steps.intro.title', bodyKey: 'tour.autoPosting.steps.intro.body' },
      { selector: '[data-tour="auto-post-period"]', titleKey: 'tour.autoPosting.steps.period.title', bodyKey: 'tour.autoPosting.steps.period.body' },
      { selector: '[data-tour="auto-post-actions"]', titleKey: 'tour.autoPosting.steps.actions.title', bodyKey: 'tour.autoPosting.steps.actions.body' },
      { selector: '[data-tour="auto-post-payroll"]', titleKey: 'tour.autoPosting.steps.payroll.title', bodyKey: 'tour.autoPosting.steps.payroll.body' },
      { selector: '[data-tour="auto-post-result"]', titleKey: 'tour.autoPosting.steps.result.title', bodyKey: 'tour.autoPosting.steps.result.body' },
    ],
  },
  {
    id: 'declarations',
    titleKey: 'tour.declarations.title',
    descriptionKey: 'tour.declarations.description',
    icon: 'pi pi-file-edit',
    route: '/declarations',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.declarations.steps.intro.title', bodyKey: 'tour.declarations.steps.intro.body' },
      { selector: '[data-tour="declarations-table"]', titleKey: 'tour.declarations.steps.table.title', bodyKey: 'tour.declarations.steps.table.body' },
    ],
  },
  {
    id: 'fixedAssets',
    titleKey: 'tour.fixedAssets.title',
    descriptionKey: 'tour.fixedAssets.description',
    icon: 'pi pi-building',
    route: '/fixed-assets',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.fixedAssets.steps.intro.title', bodyKey: 'tour.fixedAssets.steps.intro.body' },
      { selector: '[data-tour="fixed-assets-add"]', titleKey: 'tour.fixedAssets.steps.add.title', bodyKey: 'tour.fixedAssets.steps.add.body' },
      { selector: '[data-tour="fixed-assets-depreciation"]', titleKey: 'tour.fixedAssets.steps.depreciation.title', bodyKey: 'tour.fixedAssets.steps.depreciation.body' },
      { selector: '[data-tour="fixed-assets-table"]', titleKey: 'tour.fixedAssets.steps.table.title', bodyKey: 'tour.fixedAssets.steps.table.body' },
    ],
  },
  {
    id: 'vatRegisters',
    titleKey: 'tour.vatRegisters.title',
    descriptionKey: 'tour.vatRegisters.description',
    icon: 'pi pi-list',
    route: '/bookkeeping/vat-registers',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.vatRegisters.steps.intro.title', bodyKey: 'tour.vatRegisters.steps.intro.body' },
      { selector: '[data-tour="vat-registers-primary"]', titleKey: 'tour.vatRegisters.steps.primary.title', bodyKey: 'tour.vatRegisters.steps.primary.body' },
      { selector: '[data-tour="vat-registers-main"]', titleKey: 'tour.vatRegisters.steps.main.title', bodyKey: 'tour.vatRegisters.steps.main.body' },
    ],
  },
  {
    id: 'submissions',
    titleKey: 'tour.submissions.title',
    descriptionKey: 'tour.submissions.description',
    icon: 'pi pi-send',
    route: '/declarations/submissions',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.submissions.steps.intro.title', bodyKey: 'tour.submissions.steps.intro.body' },
      { selector: '[data-tour="submissions-primary"]', titleKey: 'tour.submissions.steps.primary.title', bodyKey: 'tour.submissions.steps.primary.body' },
      { selector: '[data-tour="submissions-main"]', titleKey: 'tour.submissions.steps.main.title', bodyKey: 'tour.submissions.steps.main.body' },
    ],
  },
  {
    id: 'taxSettlements',
    titleKey: 'tour.taxSettlements.title',
    descriptionKey: 'tour.taxSettlements.description',
    icon: 'pi pi-lock',
    route: '/tax-settlements',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.taxSettlements.steps.intro.title', bodyKey: 'tour.taxSettlements.steps.intro.body' },
      { selector: '[data-tour="tax-settlements-primary"]', titleKey: 'tour.taxSettlements.steps.primary.title', bodyKey: 'tour.taxSettlements.steps.primary.body' },
      { selector: '[data-tour="tax-settlements-main"]', titleKey: 'tour.taxSettlements.steps.main.title', bodyKey: 'tour.taxSettlements.steps.main.body' },
    ],
  },
  {
    id: 'comarch',
    titleKey: 'tour.comarch.title',
    descriptionKey: 'tour.comarch.description',
    icon: 'pi pi-desktop',
    route: '/comarch',
    group: 'accounting',
    steps: [
      { titleKey: 'tour.comarch.steps.intro.title', bodyKey: 'tour.comarch.steps.intro.body' },
      { selector: '[data-tour="comarch-primary"]', titleKey: 'tour.comarch.steps.primary.title', bodyKey: 'tour.comarch.steps.primary.body' },
      { selector: '[data-tour="comarch-main"]', titleKey: 'tour.comarch.steps.main.title', bodyKey: 'tour.comarch.steps.main.body' },
    ],
  },
  {
    id: 'vehicles',
    titleKey: 'tour.vehicles.title',
    descriptionKey: 'tour.vehicles.description',
    icon: 'pi pi-car',
    route: '/vehicles',
    group: 'client',
    steps: [
      { titleKey: 'tour.vehicles.steps.intro.title', bodyKey: 'tour.vehicles.steps.intro.body' },
      { selector: '[data-tour="vehicles-primary"]', titleKey: 'tour.vehicles.steps.primary.title', bodyKey: 'tour.vehicles.steps.primary.body' },
      { selector: '[data-tour="vehicles-main"]', titleKey: 'tour.vehicles.steps.main.title', bodyKey: 'tour.vehicles.steps.main.body' },
    ],
  },
  {
    id: 'fiscalReports',
    titleKey: 'tour.fiscalReports.title',
    descriptionKey: 'tour.fiscalReports.description',
    icon: 'pi pi-print',
    route: '/fiscal-reports',
    group: 'client',
    steps: [
      { titleKey: 'tour.fiscalReports.steps.intro.title', bodyKey: 'tour.fiscalReports.steps.intro.body' },
      { selector: '[data-tour="fiscal-reports-primary"]', titleKey: 'tour.fiscalReports.steps.primary.title', bodyKey: 'tour.fiscalReports.steps.primary.body' },
      { selector: '[data-tour="fiscal-reports-main"]', titleKey: 'tour.fiscalReports.steps.main.title', bodyKey: 'tour.fiscalReports.steps.main.body' },
    ],
  },
  {
    id: 'timeReports',
    titleKey: 'tour.timeReports.title',
    descriptionKey: 'tour.timeReports.description',
    icon: 'pi pi-clock',
    route: '/time-reports',
    group: 'work',
    steps: [
      { titleKey: 'tour.timeReports.steps.intro.title', bodyKey: 'tour.timeReports.steps.intro.body' },
      { selector: '[data-tour="time-reports-primary"]', titleKey: 'tour.timeReports.steps.primary.title', bodyKey: 'tour.timeReports.steps.primary.body' },
      { selector: '[data-tour="time-reports-main"]', titleKey: 'tour.timeReports.steps.main.title', bodyKey: 'tour.timeReports.steps.main.body' },
    ],
  },
  {
    id: 'messages',
    titleKey: 'tour.messages.title',
    descriptionKey: 'tour.messages.description',
    icon: 'pi pi-comments',
    route: '/messages',
    group: 'communication',
    steps: [
      { titleKey: 'tour.messages.steps.intro.title', bodyKey: 'tour.messages.steps.intro.body' },
      { selector: '[data-tour="messages-primary"]', titleKey: 'tour.messages.steps.primary.title', bodyKey: 'tour.messages.steps.primary.body' },
      { selector: '[data-tour="messages-main"]', titleKey: 'tour.messages.steps.main.title', bodyKey: 'tour.messages.steps.main.body' },
    ],
  },
];
